import { formatAmount, type TokenScale } from "@/lib/format/amount";
import type { Mission, MissionMetric } from "@/lib/content/types";
import type { AccountStatistics } from "./accounts";

export const MISSION_METRICS: MissionMetric[] = ["bets", "wagered", "rewards", "transactions"];

const AMOUNT_METRICS: MissionMetric[] = ["wagered", "rewards"];

const NOUNS: Record<MissionMetric, [string, string]> = {
  bets: ["bet", "bets"],
  transactions: ["transaction", "transactions"],
  wagered: ["", ""],
  rewards: ["", ""],
};

const COUNT = /^\d+$/;
const DECIMAL = /^\d+(\.\d+)?$/;

const toBaseUnits = (value: string, decimals: number) => {
  const trimmed = value.trim();

  if (!DECIMAL.test(trimmed)) return 0n;

  const [whole, fraction = ""] = trimmed.split(".");

  if (decimals <= 0) return BigInt(whole);

  return BigInt(whole + (fraction + "0".repeat(decimals)).slice(0, decimals));
};

export type MissionProgress = {
  configured: boolean;
  percent: number;
  remaining: string;
  complete: boolean;
};

const wholeNumber = (value: number) => (Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0);

export function missionTarget(mission: Mission): string {
  const target = wholeNumber(mission.target);

  if (target <= 0) {
    return "";
  }

  const [singular, plural] = NOUNS[mission.metric];
  const noun = target === 1 ? singular : plural;
  const figure = target.toLocaleString("en-US");

  return noun ? `${figure} ${noun}` : figure;
}

const statisticFor = (stats: AccountStatistics, metric: MissionMetric) => {
  switch (metric) {
    case "wagered":
      return stats.wagered;
    case "rewards":
      return stats.rewards;
    case "bets":
      return String(stats.bets);
    case "transactions":
      return String(stats.transactions);
  }
};

export function missionProgress(mission: Mission, stats: AccountStatistics): MissionProgress {
  const isAmount = AMOUNT_METRICS.includes(mission.metric);
  const scale: TokenScale = {
    decimals: wholeNumber(stats.decimals),
    significantDecimals: wholeNumber(stats.significantDecimals),
  };

  const raw = statisticFor(stats, mission.metric);
  const value = isAmount
    ? toBaseUnits(raw, scale.decimals)
    : COUNT.test(raw)
      ? BigInt(raw)
      : 0n;

  const target = wholeNumber(mission.target);
  const goal = isAmount ? BigInt(target) * 10n ** BigInt(scale.decimals) : BigInt(target);

  if (goal <= 0n) {
    return { configured: false, percent: 0, remaining: "", complete: false };
  }

  const left = goal > value ? goal - value : 0n;
  const percent = value >= goal ? 100 : Number((value * 10_000n) / goal) / 100;

  if (left === 0n) {
    return { configured: true, percent, remaining: "Complete", complete: true };
  }

  if (isAmount) {
    return {
      configured: true,
      percent,
      remaining: `${formatAmount(left.toString(), scale)} left`,
      complete: false,
    };
  }

  const [singular, plural] = NOUNS[mission.metric];

  return {
    configured: true,
    percent,
    remaining: `${left.toLocaleString("en-US")} ${left === 1n ? singular : plural} left`,
    complete: false,
  };
}
